/**
 * ServiceWorkerBoot — Phase 3.bis
 * Registers /sw.js and shows a discreet toast when a new SW is installed but
 * waiting to activate. User clicks → SKIP_WAITING → reload → new bundle live.
 *
 * Behavior:
 * - If SW already active at mount and a new one lands → show toast
 * - On `controllerchange` (new SW took control) → reload once (guard against loops)
 * - If activation happens silently via skipWaiting + clients.claim → no toast
 *   needed (first-time install), we just let it run.
 */
import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';

export default function ServiceWorkerBoot() {
  const [waitingSW, setWaitingSW] = useState(null);
  const reloadedRef = useRef(false);
  const autoApplyTimerRef = useRef(null);

  // Phase 5.4a-hotfix (2026-05-15) — auto-apply pending updates after a
  // short delay so users on long-lived tabs don't keep running the old
  // bundle silently (e.g. Carlos missing the toast). The toast still
  // appears for ~25s; after that the update is applied automatically.
  const armAutoApply = (sw) => {
    if (autoApplyTimerRef.current) clearTimeout(autoApplyTimerRef.current);
    autoApplyTimerRef.current = setTimeout(() => {
      try { sw.postMessage({ type: 'SKIP_WAITING' }); } catch (_) { /* noop */ }
    }, 25 * 1000);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!('serviceWorker' in navigator)) return undefined;

    // Useful console hint in case Carlos is stuck on a very old bundle.
    // Pasting the command below clears all SWs + caches and reloads.
    // eslint-disable-next-line no-console
    console.info(
      '[R.E.Mobiles] Recovery (if app feels stuck offline): ' +
      "navigator.serviceWorker.getRegistrations().then(r=>r.forEach(x=>x.unregister()));" +
      "caches.keys().then(k=>k.forEach(c=>caches.delete(c)));location.reload();",
    );

    const onLoad = () => {
      navigator.serviceWorker
        .register('/sw.js', { updateViaCache: 'none' })
        .then((reg) => {
          // A new SW is waiting right now
          if (reg.waiting && navigator.serviceWorker.controller) {
            setWaitingSW(reg.waiting);
            armAutoApply(reg.waiting);
          }

          // Future updates detected by browser
          reg.addEventListener('updatefound', () => {
            const installing = reg.installing;
            if (!installing) return;
            installing.addEventListener('statechange', () => {
              if (installing.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                  // Existing SW controls the page → this is an UPDATE
                  setWaitingSW(installing);
                  armAutoApply(installing);
                } else {
                  // First install — nothing to prompt
                }
              }
            });
          });

          // Poll for updates every 30 min (app-initiated)
          setInterval(() => reg.update().catch(() => {}), 30 * 60 * 1000);
        })
        .catch(() => { /* silent */ });

      // When the new SW actually takes over (after SKIP_WAITING) → reload once
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (reloadedRef.current) return;
        reloadedRef.current = true;
        window.location.reload();
      });

      // Phase 5.4a-hotfix (2026-05-15) — listen for SW_FORCE_RELOAD broadcast.
      // The SW now sends this on activate so older bundles (without the
      // offline interceptor) get refreshed immediately, even if the user's
      // previous SW didn't have skipWaiting and the page's old JS is the
      // one currently controlling the tab.
      navigator.serviceWorker.addEventListener('message', (event) => {
        const data = event && event.data;
        if (!data) return;
        if (data.type === 'SW_FORCE_RELOAD') {
          if (reloadedRef.current) return;
          reloadedRef.current = true;
          setTimeout(() => window.location.reload(), 100);
        }
      });
    };

    if (document.readyState === 'complete') onLoad();
    else window.addEventListener('load', onLoad, { once: true });
    return () => {
      window.removeEventListener('load', onLoad);
      if (autoApplyTimerRef.current) clearTimeout(autoApplyTimerRef.current);
    };
  }, []);

  if (!waitingSW) return null;

  const applyUpdate = () => {
    try {
      waitingSW.postMessage({ type: 'SKIP_WAITING' });
    } catch { /* noop */ }
    // controllerchange listener above triggers the reload
  };

  const dismiss = () => setWaitingSW(null);

  return (
    <div className="sw-update-toast" role="alert" data-testid="sw-update-toast">
      <div className="sw-u-icon"><RefreshCw size={18} /></div>
      <div className="sw-u-body">
        <div className="sw-u-title">Nouvelle version disponible</div>
        <div className="sw-u-sub">Cliquez pour recharger avec les dernières améliorations.</div>
      </div>
      <button
        type="button"
        onClick={applyUpdate}
        className="sw-u-cta"
        data-testid="sw-update-apply"
      >
        Recharger
      </button>
      <button
        type="button"
        onClick={dismiss}
        className="sw-u-close"
        aria-label="Ignorer"
        data-testid="sw-update-dismiss"
      >
        <X size={14} />
      </button>

      <style>{`
        .sw-update-toast {
          position: fixed;
          left: 50%;
          bottom: 80px;              /* above any PWA install prompt */
          transform: translateX(-50%);
          z-index: 9985;
          display: flex; align-items: center; gap: 12px;
          background: linear-gradient(155deg, #0f0f0f 0%, #050505 100%);
          color: #f5f5f5;
          border: 1px solid rgba(212,175,55,0.38);
          border-radius: 14px;
          padding: 12px 14px;
          box-shadow: 0 18px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,0,0,0.4),
                      0 0 20px rgba(212,175,55,0.12);
          font-family: -apple-system, "SF Pro Display", "Segoe UI", system-ui, sans-serif;
          max-width: 92vw;
          animation: swSlide .25s cubic-bezier(.2,.9,.3,1.2);
        }
        @keyframes swSlide { from { transform: translate(-50%, 16px); opacity: 0 } to { transform: translateX(-50%); opacity: 1 } }
        .sw-u-icon {
          width: 34px; height: 34px; border-radius: 10px;
          background: rgba(200,16,46,0.18); color: #d4af37;
          display: grid; place-items: center;
          border: 1px solid rgba(212,175,55,0.3);
          flex-shrink: 0;
        }
        .sw-u-icon svg { animation: swSpin 2s linear infinite; }
        @keyframes swSpin { to { transform: rotate(360deg) } }
        .sw-u-body { display: flex; flex-direction: column; min-width: 0; }
        .sw-u-title { font-weight: 700; font-size: 13.5px; letter-spacing: 0.01em; }
        .sw-u-sub { font-size: 12px; color: #aaa; max-width: 240px; }
        .sw-u-cta {
          background: linear-gradient(145deg, #d11833 0%, #8a0a1d 100%);
          color: #fff;
          border: 1px solid #d4af37;
          padding: 7px 14px;
          border-radius: 999px;
          font-size: 13px; font-weight: 600;
          cursor: pointer;
          transition: transform .15s ease, box-shadow .15s ease;
          white-space: nowrap;
        }
        .sw-u-cta:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(200,16,46,0.4); }
        .sw-u-close {
          background: transparent; border: none; color: #888; cursor: pointer;
          padding: 4px; line-height: 0;
        }
        .sw-u-close:hover { color: #fff; }
        @media (max-width: 480px) { .sw-u-sub { max-width: 160px; } }
      `}</style>
    </div>
  );
}
